
/**
 * Generates a unique cache key based on the API payload
 */
export function generateCacheKey(payload = {}) {
  if (Object.keys(payload).length === 0) {
    return 'news_default';
  }
  
  // Create a readable key from the payload
  const keyParts = [];
  
  for (const [key, value] of Object.entries(payload).sort()) {
    if (value !== undefined && value !== null) {
      keyParts.push(`${key}=${value}`);
    }
  }
  return `news_${keyParts.join('&')}`;
}

/**
 * Initializes IndexedDB database for caching
 */
export function initNewsDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('NewsCacheDB', 1);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('newsCache')) {
        const store = db.createObjectStore('newsCache', { keyPath: 'cacheKey' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
}

/**
 * Stores data in IndexedDB cache
 */
export async function cacheNewsData(cacheKey, payload, newsData) {
  try {
    const db = await initNewsDB();
    const transaction = db.transaction(['newsCache'], 'readwrite');
    const store = transaction.objectStore('newsCache');
    
    const cacheItem = {
      cacheKey: cacheKey,
      payload: payload,
      data: newsData,
      timestamp: Date.now()
    };
    
    store.put(cacheItem);
    
    await new Promise((resolve, reject) => {
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(transaction.error);
    });
    
  } catch (error) {
    console.warn('Caching failed:', error);
    throw error; 
  }
}

/**
 * Retrieves cached data from IndexedDB
 */
export async function getCachedNews(cacheKey) {
  try {
    const db = await initNewsDB();
    const transaction = db.transaction(['newsCache'], 'readonly');
    const store = transaction.objectStore('newsCache');
    const request = store.get(cacheKey);
    
    return await new Promise((resolve) => {
      request.onsuccess = () => {
        const cachedData = request.result;
        if (cachedData && (Date.now() - cachedData.timestamp) < (60 * 60 * 1000)) {
          resolve(cachedData.data);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => resolve(null);
    });
    
  } catch (error) {
    console.warn('Cache retrieval failed:', error);
    return null;
  }
}

/**
 * Cleans up expired cache entries (older than 5 minutes)
 */
export async function cleanupExpiredCacheEfficient() {
  try {
    const db = await initNewsDB();
    const transaction = db.transaction(['newsCache'], 'readwrite');
    const store = transaction.objectStore('newsCache');
    const timestampIndex = store.index('timestamp');
    
    const fiveMinutesAgo = Date.now() - (60 * 60 * 1000);
    let deletedCount = 0;
    
    // Open cursor for entries older than 5 minutes
    const request = timestampIndex.openCursor(IDBKeyRange.upperBound(fiveMinutesAgo));
    
    return await new Promise((resolve, reject) => {
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          deletedCount++;
          cursor.continue();
        } else {
        //   console.log(`Cleaned up ${deletedCount} expired cache entries`);
          resolve(deletedCount);
        }
      };
      
      request.onerror = () => reject(request.error);
      transaction.onerror = () => reject(transaction.error);
    });
    
  } catch (error) {
    console.error('Error cleaning expired cache:', error);
    throw error;
  }
}


/**
 * Cleans up the news cache
 */
export async function clearAllCacheEntries() {
  try {
    const db = await initNewsDB();
    const transaction = db.transaction(['newsCache'], 'readwrite');
    const store = transaction.objectStore('newsCache');
    
    const request = store.clear();
    
    return await new Promise((resolve, reject) => {
      request.onsuccess = () => {
        console.log('All cache entries cleared');
        resolve();
      };
      
      request.onerror = () => reject(request.error);
      transaction.onerror = () => reject(transaction.error);
    });
    
  } catch (error) {
    console.error('Error clearing all cache entries:', error);
    throw error;
  }
}